// src/components/ContentQualityDashboardModal.js

import React, { useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import styles from './HealthDashboardModal.module.css'; // Reuse the existing CSS
import { formatMarkdownContent } from '../utils/HelperFunctions'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes, faCamera, faMicrophone, faFilm, faLightbulb,
    faStar, faHandshake, faComments, faThumbsUp, faBullhorn,
    faUserCheck,faBookOpen
} from '@fortawesome/free-solid-svg-icons';

// Formats keys like "impact_on_gtm_strategy" into "Impact On Gtm Strategy"
const formatKeyToTitle = (key) => {
    return key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/\b\w/g, char => char.toUpperCase());
};

// --- NEW HELPER ---
// Checks if an object has only numeric keys, making it "array-like".
const isArrayLikeObject = (value) => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }
    const keys = Object.keys(value);
    return keys.length > 0 && keys.every(key => /^\d+$/.test(key));
};


// --- UPGRADED DYNAMIC COMPONENT ---
// Now intelligently renders strings as paragraphs and array-like objects as bulleted lists.
const DynamicObjectDisplay = ({ data, isNested = false }) => {
    // Base case for non-objects (strings, numbers, etc.)
    if (typeof data !== 'object' || data === null) {
        return <p>{String(data)}</p>;
    }

    const containerClass = isNested 
        ? `${styles.dynamicObjectContainer} ${styles.nestedObjectContainer}` 
        : styles.dynamicObjectContainer;

    return (
        <div className={containerClass}>
            {Object.entries(data).map(([key, value]) => (
                <div key={key} className={styles.dynamicObjectEntry}>
                    {/* Don't show a subtitle for numeric keys in an array-like object */}
                    {isArrayLikeObject(data) ? null : <h4>{formatKeyToTitle(key)}</h4>}
                    
                    {(() => {
                        // Case 1: Value is an array or looks like one.
                        if (Array.isArray(value) || isArrayLikeObject(value)) {
                            return (
                                <ul>
                                    {Object.values(value).map((item, index) => (
                                        <li key={index}>
                                            {/* RECURSIVE CHECK: If the list item is an object, render it with another DynamicObjectDisplay */}
                                            {typeof item === 'object' && item !== null ? (
                                                <DynamicObjectDisplay data={item} isNested={true} />
                                            ) : (
                                                // Otherwise, just render the string/number value
                                                String(item)
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            );
                        }
                        // Case 2: Value is a non-array-like object. Render recursively.
                        if (typeof value === 'object' && value !== null) {
                            return <DynamicObjectDisplay data={value} isNested={true} />;
                        }
                        // Case 3: Value is a simple primitive. Render a paragraph.
                        return <p>{String(value)}</p>;
                    })()}
                </div>
            ))}
        </div>
    );
};

// Helper to map tier names to CSS classes
const getTierClassName = (tier) => {
    // ... (rest of the function is unchanged)
    switch (tier?.toLowerCase()) {
        case 'prime':
        case 'excellent':
            return styles.prime;
        case 'promising':
        case 'good':
            return styles.promising;
        case 'acceptable':
            return styles.acceptable;
        case 'high-risk':
        case 'poor':
            return styles.highRisk;
        default:
            return '';
    }
};

const ContentQualityDashboardModal = ({ isOpen, onClose, data }) => {
    console.log("[ DEBUG 1. Display Layer] Props received by modal:", data);
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, handleKeyDown]);

    if (!isOpen) return null;

    const score = data?.total_weighted_score ?? 0;
    const tier = data?.tier ?? 'N/A';
    const formattedConclusion = formatMarkdownContent(data?.conclusion);

    const breakdown = data?.scoring_breakdown;
    const coreQuality = breakdown?.core_video_quality;
    const contentDelivery = breakdown?.content_and_delivery;
    const audienceInteraction = breakdown?.audience_interaction;
    

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={`${styles.modalContent} ${styles.qualityModalContent}`} onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h3>Content Quality Analysis</h3>
                    <button onClick={onClose} className={styles.closeButton} aria-label="Close">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {!data ? (
                     <div className={styles.errorContainer}>
                        <h4>Analysis Not Available</h4>
                        <p>Aggregated content quality data has not been generated for this influencer yet.</p>
                    </div>
                ) : (
                    <>
                        <div className={styles.mainGrid}>
                            <div className={styles.scoreCard}>
                                <div className={`${styles.tierBadge} ${getTierClassName(tier)}`}>
                                    {tier}
                                </div>
                                <div className={styles.scoreValue}>{score.toFixed(2)}</div>
                                <div className={styles.scoreLabel}>Overall Content Quality</div>
                            </div>
                            <div className={styles.rationaleCard}>
                                <h4>Analyst Conclusion</h4>
                                <div className={styles.markdownContent}>
                                   <ReactMarkdown>{formattedConclusion}</ReactMarkdown>
                                </div>
                            </div>
                        </div>
                        
                        {data?.product_analysis && (
                           <>
                                <h4>Product Showcase Analysis</h4>
                                <div className={styles.rationaleCard}>
                                   <DynamicObjectDisplay data={data.product_analysis} />
                                </div>
                           </>
                        )}

                        <h4>Core Video Quality</h4>
                        <div className={styles.kpiGridContentQuality}>
                            <div className={styles.kpiCard}>
                                <FontAwesomeIcon icon={faCamera} className={styles.kpiIcon} />
                                <div className={styles.kpiValue}>
                                    {coreQuality?.visual_quality?.score != null && coreQuality.visual_quality.score > 0
                                        ? `${coreQuality.visual_quality.score.toFixed(1)}/5`
                                        : 'N/A'
                                    }
                                </div>
                                <div className={styles.kpiLabel}>Visual Quality</div>
                                <div className={styles.kpiDetail}>{coreQuality?.visual_quality?.value ?? 'N/A'}</div>
                            </div>

                            <div className={styles.kpiCard}>
                                <FontAwesomeIcon icon={faMicrophone} className={styles.kpiIcon} />
                                <div className={styles.kpiValue}>
                                    {coreQuality?.audio_quality?.score != null && coreQuality.audio_quality.score > 0
                                        ? `${coreQuality.audio_quality.score.toFixed(1)}/5`
                                        : 'N/A'
                                    }
                                </div>
                                <div className={styles.kpiLabel}>Audio Quality</div>
                                <div className={styles.kpiDetail}>{coreQuality?.audio_quality?.value ?? 'N/A'}</div>
                            </div>

                            <div className={styles.kpiCard}>
                                <FontAwesomeIcon icon={faFilm} className={styles.kpiIcon} />
                                <div className={styles.kpiValue}>
                                    {coreQuality?.editing?.score != null && coreQuality.editing.score > 0
                                        ? `${coreQuality.editing.score.toFixed(1)}/5`
                                        : 'N/A'
                                    }
                                </div>
                                <div className={styles.kpiLabel}>Editing & Pacing</div>
                                <div className={styles.kpiDetail}>{coreQuality?.editing?.value ?? 'N/A'}</div>
                            </div>
                        </div>

                        <h4>Content & Delivery</h4>
                        <div className={`${styles.kpiGridContentQuality} ${styles.fourColumnGrid}`}>
                            <div className={styles.kpiCard}>
                                <FontAwesomeIcon icon={faLightbulb} className={styles.kpiIcon} />
                                <div className={styles.kpiValue}>
                                {contentDelivery?.clarity_and_value?.score != null && contentDelivery.clarity_and_value.score > 0
                                    ? `${contentDelivery.clarity_and_value.score.toFixed(1)}/5`
                                    : 'N/A'
                                }
                                </div>
                                <div className={styles.kpiLabel}>Clarity & Value</div>
                                <div className={styles.kpiDetail}>{contentDelivery?.clarity_and_value?.value ?? 'N/A'}</div>
                            </div>

                            <div className={styles.kpiCard}>
                                <FontAwesomeIcon icon={faStar} className={styles.kpiIcon} />
                                <div className={styles.kpiValue}>
                                {contentDelivery?.charisma?.score != null && contentDelivery.charisma.score > 0
                                    ? `${contentDelivery.charisma.score.toFixed(1)}/5`
                                    : 'N/A'
                                }
                                </div>
                                <div className={styles.kpiLabel}>Charisma & Presence</div>
                                <div className={styles.kpiDetail}>{contentDelivery?.charisma?.value ?? 'N/A'}</div>
                            </div>

                            <div className={styles.kpiCard}>
                                <FontAwesomeIcon icon={faUserCheck} className={styles.kpiIcon} />
                                <div className={styles.kpiValue}>
                                {contentDelivery?.authenticity?.score != null && contentDelivery.authenticity.score > 0
                                    ? `${contentDelivery.authenticity.score.toFixed(1)}/5`
                                    : 'N/A'
                                }
                                </div>
                                <div className={styles.kpiLabel}>Authenticity</div>
                                <div className={styles.kpiDetail}>{contentDelivery?.authenticity?.value ?? 'N/A'}</div>
                            </div>

                            <div className={styles.kpiCard}>
                                <FontAwesomeIcon icon={faBookOpen} className={styles.kpiIcon} />
                                <div className={styles.kpiValue}>
                                {contentDelivery?.storytelling?.score != null && contentDelivery.storytelling.score > 0
                                    ? `${contentDelivery.storytelling.score.toFixed(1)}/5`
                                    : 'N/A'
                                }
                                </div>
                                <div className={styles.kpiLabel}>Storytelling</div>
                                <div className={styles.kpiDetail}>{contentDelivery?.storytelling?.value ?? 'N/A'}</div>
                            </div>
                        </div>

                        <h4>Audience Interaction</h4>
                        <div className={`${styles.kpiGridContentQuality} ${styles.fourColumnGrid}`}>
                            <div className={styles.kpiCard}>
                                <FontAwesomeIcon icon={faComments} className={styles.kpiIcon} />
                                <div className={styles.kpiValue}>
                                {audienceInteraction?.comment_sentiment?.score != null && audienceInteraction.comment_sentiment.score > 0
                                    ? `${audienceInteraction.comment_sentiment.score.toFixed(1)}/5`
                                    : 'N/A'
                                }
                                </div>
                                <div className={styles.kpiLabel}>Comment Sentiment</div>
                                <div className={styles.kpiDetail}>{audienceInteraction?.comment_sentiment?.value ?? 'N/A'}</div>
                            </div>

                            <div className={styles.kpiCard}>
                                <FontAwesomeIcon icon={faHandshake} className={styles.kpiIcon} />
                                <div className={styles.kpiValue}>
                                {audienceInteraction?.influencer_engagement?.score != null && audienceInteraction.influencer_engagement.score > 0
                                    ? `${audienceInteraction.influencer_engagement.score.toFixed(1)}/5`
                                    : 'N/A'
                                }
                                </div>
                                <div className={styles.kpiLabel}>Influencer Engagement</div>
                                <div className={styles.kpiDetail}>{audienceInteraction?.influencer_engagement?.value ?? 'N/A'}</div>
                            </div>

                            <div className={styles.kpiCard}>
                                <FontAwesomeIcon icon={faThumbsUp} className={styles.kpiIcon} />
                                <div className={styles.kpiValue}>
                                {audienceInteraction?.likes_to_views_ratio?.score != null && audienceInteraction.likes_to_views_ratio.score > 0
                                    ? `${audienceInteraction.likes_to_views_ratio.score.toFixed(1)}/5`
                                    : 'N/A'
                                }
                                </div>
                                <div className={styles.kpiLabel}>Likes-to-Views</div>
                                <div className={styles.kpiDetail}>{audienceInteraction?.likes_to_views_ratio?.value ?? 'N/A'}</div>
                            </div>

                            <div className={styles.kpiCard}>
                                <FontAwesomeIcon icon={faBullhorn} className={styles.kpiIcon} />
                                <div className={styles.kpiValue}>
                                {audienceInteraction?.call_to_action?.score != null && audienceInteraction.call_to_action.score > 0
                                    ? `${audienceInteraction.call_to_action.score.toFixed(1)}/5`
                                    : 'N/A'
                                }
                                </div>
                                <div className={styles.kpiLabel}>Call to Action</div>
                                <div className={styles.kpiDetail}>{audienceInteraction?.call_to_action?.value ?? 'N/A'}</div>
                            </div>
                        </div>                    
                    </>
                )}
            </div>
        </div>
    );
};

export default ContentQualityDashboardModal;